import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import "./ResinJourneyScroll.css";

gsap.registerPlugin(ScrollTrigger);

const SCRUB_JOURNEY_ID = "journey-scrub";
/** First share of scroll: black veil + title fade in place; then the film scrubs (0→1). */
const TALE_REVEAL_PHASE = 0.3;
/** Start downloading the frames when the section is within this many viewports of the screen. */
const PREFETCH_ROOT_MARGIN = "150% 0px";

/**
 * The film is a still-frame sequence (public/media/process-journey/f_001.webp …), drawn
 * on a canvas as the user scrolls. A <video> scrubbed through currentTime never painted a
 * frame on iPhones (black screen under the captions); decoding images needs no media
 * pipeline, autoplay permission or byte-range support, so it behaves the same everywhere.
 */
const FRAME_COUNT = 73;
const FRAME_WIDTH = 1280;
const FRAME_HEIGHT = 704;
const FRAME_DIR = `${process.env.PUBLIC_URL || ""}/media/process-journey`;

/** @param {number} index 0-based */
function frameUrl(index) {
  return `${FRAME_DIR}/f_${String(index + 1).padStart(3, "0")}.webp`;
}

/** Film progress (0–1) windows for each step — matches requested % bands. */
const CAPTION_SEGMENTS = [
  { start: 0, end: 0.2 },
  { start: 0.2, end: 0.5 },
  { start: 0.5, end: 0.8 },
  { start: 0.8, end: 1 },
];

/**
 * @param {number} filmP
 * @param {number} index
 */
function segmentVisibilityOpacity(filmP, index) {
  const seg = CAPTION_SEGMENTS[index];
  if (!seg) {
    return 0;
  }
  const { start, end } = seg;
  const width = end - start;
  const edge = Math.max(0.025, Math.min(0.09, width * 0.2));
  if (filmP <= start || filmP >= end) {
    return 0;
  }
  if (filmP < start + edge) {
    return (filmP - start) / edge;
  }
  if (filmP > end - edge) {
    return (end - filmP) / edge;
  }
  return 1;
}

export default function ResinJourneyScroll() {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const veilRef = useRef(null);
  const titleRef = useRef(null);
  const progressFillRef = useRef(null);
  const progressBarRef = useRef(null);
  const scrubDriverTweenRef = useRef(null);

  useLayoutEffect(() => {
    gsap.ticker.lagSmoothing(0);

    const canvas = canvasRef.current;
    const veilEl = veilRef.current;
    const titleEl = titleRef.current;
    const progressFill = progressFillRef.current;
    const progressBar = progressBarRef.current;
    const wrapper = wrapperRef.current;

    if (!canvas || !veilEl || !titleEl || !progressFill || !progressBar || !wrapper) {
      return undefined;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return undefined;
    }
    canvas.width = FRAME_WIDTH;
    canvas.height = FRAME_HEIGHT;

    // ── Frame store ───────────────────────────────────────────────────────────────
    /** @type {(HTMLImageElement | null)[]} decoded frames by index; null until loaded */
    const frames = new Array(FRAME_COUNT).fill(null);
    let targetFrame = 0;
    let drawnFrame = -1;
    let drawScheduled = false;
    let aborted = false;

    /** Nearest loaded frame to `index`, preferring the earlier one on ties (frames arrive in order). */
    const nearestLoaded = (index) => {
      if (frames[index]) return index;
      for (let d = 1; d < FRAME_COUNT; d += 1) {
        if (index - d >= 0 && frames[index - d]) return index - d;
        if (index + d < FRAME_COUNT && frames[index + d]) return index + d;
      }
      return -1;
    };

    const paint = () => {
      drawScheduled = false;
      if (aborted) return;
      const index = nearestLoaded(targetFrame);
      if (index < 0 || index === drawnFrame) return;
      ctx.drawImage(frames[index], 0, 0, FRAME_WIDTH, FRAME_HEIGHT);
      drawnFrame = index;
      canvas.dataset.frame = String(index);
    };

    const requestPaint = () => {
      if (drawScheduled) return;
      drawScheduled = true;
      window.requestAnimationFrame(paint);
    };

    const loadFrames = () => {
      for (let i = 0; i < FRAME_COUNT; i += 1) {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          if (aborted) return;
          frames[i] = img;
          // Only repaint when this frame improves what is on screen.
          if (drawnFrame < 0 || Math.abs(i - targetFrame) < Math.abs(drawnFrame - targetFrame)) {
            requestPaint();
          }
        };
        img.src = frameUrl(i);
      }
    };

    // ── Scroll choreography ──────────────────────────────────────────────────────
    const setProgressAria = (progress) => {
      progressBar.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
    };

    const tearDownJourney = () => {
      const st = ScrollTrigger.getById(SCRUB_JOURNEY_ID);
      if (st) {
        st.kill();
      }
      if (scrubDriverTweenRef.current) {
        scrubDriverTweenRef.current.kill();
        scrubDriverTweenRef.current = null;
      }
    };

    const captions = gsap.utils.toArray(wrapper.querySelectorAll(".process-journey-scroll__caption"));

    gsap.set(veilEl, { opacity: 1 });
    gsap.set(titleEl, { opacity: 1 });
    gsap.set(captions, { opacity: 0 });
    gsap.set(progressFill, { scaleX: 0 });

    const drive = { p: 0 };
    scrubDriverTweenRef.current = gsap.to(drive, {
      p: 1,
      duration: 1,
      ease: "none",
      paused: true,
      onUpdate: () => {
        const clampedP = Math.min(1, Math.max(0, drive.p));

        const taleT = Math.min(1, clampedP / TALE_REVEAL_PHASE);
        const coverOpacity = 1 - taleT;
        gsap.set(veilEl, { opacity: coverOpacity });
        gsap.set(titleEl, { opacity: coverOpacity });

        const filmP =
          clampedP <= TALE_REVEAL_PHASE ? 0 : (clampedP - TALE_REVEAL_PHASE) / (1 - TALE_REVEAL_PHASE);
        targetFrame = Math.round(filmP * (FRAME_COUNT - 1));
        requestPaint();

        captions.forEach((el, i) => {
          gsap.set(el, { opacity: segmentVisibilityOpacity(filmP, i) });
        });
        gsap.set(progressFill, { scaleX: clampedP });
        setProgressAria(clampedP);
      },
    });

    ScrollTrigger.create({
      id: SCRUB_JOURNEY_ID,
      trigger: wrapper,
      start: "top top",
      end: "bottom bottom",
      animation: scrubDriverTweenRef.current,
      scrub: 0.35,
    });
    ScrollTrigger.refresh();

    const onResize = () => {
      ScrollTrigger.refresh();
    };
    window.addEventListener("resize", onResize);

    // Sections above this one render lazily (content-visibility) and images load late, so
    // the wrapper's page offset moves after the trigger is built; re-measure when it does.
    let refreshTimer = null;
    let lastBodyHeight = document.body.offsetHeight;
    const bodyObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(() => {
            const next = document.body.offsetHeight;
            if (next === lastBodyHeight) return;
            lastBodyHeight = next;
            window.clearTimeout(refreshTimer);
            refreshTimer = window.setTimeout(() => ScrollTrigger.refresh(), 120);
          })
        : null;
    bodyObserver?.observe(document.body);

    // ── Prefetch ─────────────────────────────────────────────────────────────────
    let observer = null;
    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((e) => e.isIntersecting)) return;
          observer.disconnect();
          observer = null;
          loadFrames();
        },
        { rootMargin: PREFETCH_ROOT_MARGIN },
      );
      observer.observe(wrapper);
    } else {
      loadFrames();
    }

    return () => {
      aborted = true;
      observer?.disconnect();
      bodyObserver?.disconnect();
      window.clearTimeout(refreshTimer);
      window.removeEventListener("resize", onResize);
      tearDownJourney();
    };
  }, []);

  return (
    <div className="process-journey-scroll">
      <section
        className="process-journey-scroll__wrapper video-scroll-wrapper"
        ref={wrapperRef}
        aria-label="Studio process, scroll-driven film"
      >
        <div className="process-journey-scroll__sticky">
          <canvas
            ref={canvasRef}
            className="process-journey-scroll__film"
            width={FRAME_WIDTH}
            height={FRAME_HEIGHT}
            style={{ backgroundImage: `url(${frameUrl(0)})` }}
            aria-hidden="true"
          />

          <div className="process-journey-scroll__caption-stack" aria-live="polite">
            <article
              className="process-journey-scroll__caption process-journey-scroll__caption--step1"
              data-step="1"
              aria-label="Step 1: The Foundation"
            >
              <div className="process-journey-scroll__caption-panel">
                <p className="process-journey-scroll__caption-kicker">Step 1 · The Foundation</p>
                <h3 className="process-journey-scroll__caption-title">The Precision Base</h3>
                <p className="process-journey-scroll__caption-sub">
                  Every masterpiece begins with a sterile, level canvas. We select high-grade silicone molds to
                  ensure your art takes a flawless architectural shape.
                </p>
              </div>
            </article>

            <article
              className="process-journey-scroll__caption process-journey-scroll__caption--step2"
              data-step="2"
              aria-label="Step 2: The Artistic Infusion"
            >
              <div className="process-journey-scroll__caption-panel">
                <p className="process-journey-scroll__caption-kicker">Step 2 · The Artistic Infusion</p>
                <h3 className="process-journey-scroll__caption-title">Chromatic Alchemy</h3>
                <p className="process-journey-scroll__caption-sub">
                  Hand-mixed pigments meet premium resin. We layer deep oceanic turquoises and crisp whites to
                  create a 3D depth that mimics the natural flow of water.
                </p>
              </div>
            </article>

            <article
              className="process-journey-scroll__caption process-journey-scroll__caption--step3"
              data-step="3"
              aria-label="Step 3: The Curing Ritual"
            >
              <div className="process-journey-scroll__caption-panel">
                <p className="process-journey-scroll__caption-kicker">Step 3 · The Curing Ritual</p>
                <h3 className="process-journey-scroll__caption-title">The Patient Cure</h3>
                <p className="process-journey-scroll__caption-sub">
                  Time is our most vital ingredient. Controlled temperatures allow the chemical bond to strengthen,
                  locking in clarity and ensuring a glass-like durability that lasts a lifetime.
                </p>
              </div>
            </article>

            <article
              className="process-journey-scroll__caption process-journey-scroll__caption--step4"
              data-step="4"
              aria-label="Step 4: The Final Transformation"
            >
              <div className="process-journey-scroll__caption-panel">
                <p className="process-journey-scroll__caption-kicker">Step 4 · The Final Transformation</p>
                <h3 className="process-journey-scroll__caption-title">Artisan Refinement</h3>
                <p className="process-journey-scroll__caption-sub">
                  From raw pour to polished perfection. We hand-place organic elements and apply a final flood coat
                  to deliver a mirror-finish that invites the touch.
                </p>
              </div>
            </article>
          </div>

          <div className="process-journey-scroll__tale-veil" ref={veilRef} aria-hidden="true" />

          <div className="process-journey-scroll__tale-title">
            <h2 className="process-journey-scroll__tale-title-text" ref={titleRef}>
              The Journey begins...
            </h2>
          </div>

          <div
            className="process-journey-scroll__progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={0}
            ref={progressBarRef}
          >
            <div className="process-journey-scroll__progress-fill" ref={progressFillRef} />
          </div>
        </div>
      </section>
    </div>
  );
}
